import { hasConsented } from "./cookie-consent"
import { parseExperimentAssignments } from "./experiment-attribution"

export const GA4_MEASUREMENT_ID = "G-LYCJCLWQ8T"
export const GA4_EXPERIMENT_USER_PROPERTY = "experiment_variant"

const MAX_USER_PROPERTY_VALUE_LENGTH = 36

let initialized = false
let pendingUserId: string | undefined
let reportedExposures = new Set<string>()

function getGtag(): NonNullable<Window["gtag"]> {
    window.dataLayer ??= []
    window.gtag ??= (...args: any[]) => {
        window.dataLayer?.push(args)
    }
    return window.gtag
}

// GA4 user-property values are limited to 36 characters. Preserve the complete,
// sorted assignment set when it fits; never silently report only a subset.
function getExperimentVariantUserProperty(assignments: Record<string, string>): string | undefined {
    const value = Object.entries(assignments)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([experiment, variant]) => `${experiment}:${variant}`)
        .join(",")

    return value && value.length <= MAX_USER_PROPERTY_VALUE_LENGTH ? value : undefined
}

function flushGoogleAnalyticsIdentity() {
    if (!hasConsented("analytics")) return

    const gtag = getGtag()
    const assignments = parseExperimentAssignments()
    const experimentVariant = getExperimentVariantUserProperty(assignments)

    gtag("consent", "update", { analytics_storage: "granted" })
    if (!initialized) {
        gtag("js", new Date())
        initialized = true
    }

    gtag("config", GA4_MEASUREMENT_ID, {
        send_page_view: false,
        ...(pendingUserId && { user_id: pendingUserId }),
        user_properties: {
            [GA4_EXPERIMENT_USER_PROPERTY]:
                pendingUserId && experimentVariant ? experimentVariant : null,
        },
    })

    // Existing-consent initialization runs before auth. Exposure is
    // user-scoped, so wait until identify() supplies the authenticated ID.
    if (!pendingUserId) return

    for (const [experiment, variant] of Object.entries(assignments)) {
        const exposureKey = `${pendingUserId}:${experiment}:${variant}`
        if (reportedExposures.has(exposureKey)) continue

        gtag("event", "experiment_exposure", {
            experiment,
            variant,
        })
        reportedExposures.add(exposureKey)
    }
}

export function syncGoogleAnalyticsConsent() {
    if (hasConsented("analytics")) {
        flushGoogleAnalyticsIdentity()
        return
    }

    window.gtag?.("consent", "update", { analytics_storage: "denied" })
}

export function identifyInGoogleAnalytics(userId: string) {
    pendingUserId = userId
    flushGoogleAnalyticsIdentity()
}

export function trackGoogleAnalyticsEvent(name: string, parameters?: Record<string, any>) {
    if (!hasConsented("analytics")) return

    getGtag()("event", name, parameters)
}

export function trackGoogleAnalyticsExperimentExposure(
    experiment: string,
    variant: string,
    parameters?: Record<string, any>,
) {
    if (!hasConsented("analytics")) return

    const experimentVariant = `${experiment}:${variant}`
    const gtag = getGtag()
    if (experimentVariant.length <= MAX_USER_PROPERTY_VALUE_LENGTH) {
        gtag("set", "user_properties", {
            [GA4_EXPERIMENT_USER_PROPERTY]: experimentVariant,
        })
    }
    gtag("event", "experiment_exposure", {
        experiment,
        variant,
        ...parameters,
    })
}

export function resetGoogleAnalyticsIdentity() {
    pendingUserId = undefined
    if (!hasConsented("analytics")) return

    getGtag()("config", GA4_MEASUREMENT_ID, {
        send_page_view: false,
        user_id: null,
        user_properties: {
            [GA4_EXPERIMENT_USER_PROPERTY]: null,
        },
    })
}

export function resetGoogleAnalyticsStateForTests() {
    initialized = false
    pendingUserId = undefined
    reportedExposures = new Set<string>()
}
